import {cloneDeep} from 'lodash';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {firstValueFrom, Observable} from 'rxjs';
import {AuthActions, IUser} from '../../models/auth';
import {Store} from '@ngxs/store';
import {StorageService} from '../../services/storage/storage.service';
import {PlanActions, PlanState} from '../../models/plan';
import {ClinicActions, ClinicState} from '../../models/clinic';
import {IResponseSubscriptionValidation, ValidationSubscription} from '../../class/helper/validation-subscription.class';
import {SubscriptionDTO} from '../../dtos/billing';
import {UserState} from '../../models/user';
import {UserService} from '../../services/user/user.service';
import {ICredentials} from '../../interfaces/credentials';
import {LocalStorageEnum} from '../../enums/utils';

@Injectable({
  providedIn: 'root'
})
export class HasActiveSubscriptionGuard implements CanActivateChild {
  constructor(
    private store: Store,
    private router: Router,
    private storageService: StorageService,
    private userService: UserService,
  ) {
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return new Promise<boolean>(async (resolve: (value: boolean) => any, reject: (reason: boolean) => any) => {
      // TODO: Agregar la validación para cunado la suscripción sea isBeta (propiedad agregada en el modelo de suscripción)
      // Debería mandar a responder las preguntas para poder obtener otro mes gratis.
      // if (subscription.status !== 'active' && subscription.isBeta === true) Mandar a responder las preguntas
      // En caso contrario dar acceso a la plataforma.

      const paramClinicId = childRoute.params.clinicId;
      const userAndToken = this.storageService.getLocalItem<{ user: IUser, token: string }>('user');
      if (!paramClinicId || !userAndToken || !userAndToken?.token) {
        await this.router.navigate(['auth/new/landing']);
        resolve(false);
        return;
      }
      let subscriptionActive: SubscriptionDTO;
      try {
        await firstValueFrom((this.store.dispatch(new PlanActions.GetSubscriptionActive(paramClinicId))));
        subscriptionActive = cloneDeep(await firstValueFrom((this.store.selectOnce(PlanState.getSubscriptionActive))));
        let clinic = cloneDeep(await firstValueFrom((this.store.selectOnce(ClinicState.getClinicSelected))));
        if (!subscriptionActive) {
          await this.router.navigate(['auth/new/landing']);
          resolve(false);
          return;
        }

        if (!clinic) {
          await firstValueFrom((this.store.dispatch(new ClinicActions.Get(paramClinicId))));
          clinic = cloneDeep(await firstValueFrom((this.store.selectOnce(ClinicState.getClinicSelected))));
        }

        const validationSubscription = new ValidationSubscription(userAndToken.user, subscriptionActive, clinic);
        const response: IResponseSubscriptionValidation = validationSubscription.checkSubscriptionPastDue();
        // Aquí no se debe comprobar los limites de la clínica.
        // const responseExceedPatients: IResponseSubscriptionValidation = validationSubscription.checkExceedPatients();
        if (!response.valid) {
          await this.router.navigate(response.route ?? ['auth/new/landing']);
          return resolve(false);
        }

        // Para cuando es usuario beta, por ahora la suscripción del usuario beta siempre será trial
        // quitamos la validación de que sea diferente de canceled subscriptionActive.status !== 'canceled' porque en desarrollo
        // stripe cancela las suscripciones
        if (subscriptionActive.status !== 'active' && subscriptionActive.isBeta) {
          await this.router.navigate([`clinic/${paramClinicId}/plans/beta`]);
          return resolve(false);
        }
        resolve(true);
      } catch (e ) {
        if (e.message === 'Cannot access this resource') {
          await this.router.navigate([`auth/login`]);
          return resolve(false);
        }
        const clinics: any[] =  this.store.selectSnapshot(UserState.getUserClinics);
        const clinicFound = clinics.find(c => c._id === paramClinicId);
        if (!clinicFound) return resolve(false);
        if (clinicFound.active) return resolve(true);
        const user: IUser = this.storageService.getLocalItem<ICredentials>(LocalStorageEnum.USER)?.user;

        // si es un usuario beta
        await firstValueFrom(this.store.dispatch(new AuthActions.IsAuthBetaUser()));
        const isBetaUser: string = this.storageService.getLocalItem(LocalStorageEnum.IS_BETA_USER); // TRUE || FALSE
        if (isBetaUser && isBetaUser === 'TRUE') {
          await this.router.navigate([`clinic/${paramClinicId}/plans/beta`]);
          return resolve(false);
        }

        const isOwner: boolean = user._id === clinicFound.ownerId;
        await firstValueFrom((this.store.dispatch(new AuthActions.GetUserMe(paramClinicId))));

        if (!isOwner) {
          await this.router.navigate([`auth/not-allowed-access?clinic=${clinicFound.name}`]);
          return resolve(false);
        }

        await this.router.navigate([`clinic/${paramClinicId}/plans/select`]);
        resolve(false);
      }
    });
  }

  private innerThrow(message: string): void {
    throw new Error(message);
  }
}
